/**
 * AuthContext — keeps the currently-authenticated owner / manager in
 * React state and exposes login/logout/refresh helpers.
 *
 * Cookies are httpOnly so we cannot read them from JS — instead we ping
 * /api/auth/me to detect an active session.
 *
 * ⚑ MAIS ON NE LE DEMANDE QUE LÀ OÙ QUELQU'UN PEUT ÊTRE CONNECTÉ (12/09/2026).
 *
 * Mesuré au navigateur sur les 77 adresses du site public — sept langues,
 * onze pages : CHACUNE appelait `/api/auth/me` et recevait **401**. Le 401 est
 * la réponse juste : personne n'est connecté sur un site public, il n'y a même
 * pas de porte pour l'être. Mais le navigateur imprime « Failed to load
 * resource: 401 » en rouge dans la console, et il le fait chez tout visiteur,
 * à chaque page. Deux coûts, et le second est le plus cher :
 *
 *   · un aller-retour réseau par page, pour une réponse toujours identique ;
 *   · une erreur rouge permanente dans la console du site public — c'est la
 *     PREMIÈRE chose que voit quiconque ouvre les outils de développement sur
 *     la production, et c'est le bruit qui cache les vraies erreurs. Une
 *     console qui crie tout le temps ne prévient plus de rien.
 *
 * Le provider enveloppe toute l'application (`App.js`), mais **trois fichiers
 * seulement** lisent ce contexte, et tous vivent derrière `/admin` :
 * `AdminLogin`, `AdminDashboard`, `AccountPasswordEditor`. Le Chantier, lui, a
 * sa propre clef portée à la main (`ChantierBoard`, `getToken`) et ne passe pas
 * par ici.
 *
 * On garde donc le provider global — le retirer casserait `useAuth` — et on
 * déplace la QUESTION : elle ne part que sur une surface d'authentification.
 * Partout ailleurs l'état part directement à « anonyme », ce qui est vrai.
 *
 * ⚠ Et la question se repose à la NAVIGATION, pas seulement au montage : on
 * lit le chemin courant par `useLocation`, si bien qu'un passage côté client
 * vers `/admin` déclenche la vérification qu'un rechargement aurait faite.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

import { BACKEND_URL } from "@/lib/medardApi";
import { detailText } from "@/lib/errorDetail";

const API = BACKEND_URL;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

// ⚠ CE FICHIER PORTAIT LA SECONDE RÉDACTION DE LA RÈGLE, et elle ne disait pas
// la même chose que la première. Son `formatError` ne cassait PAS l'écran — il
// joignait les `msg` d'un 422 — mais il affichait par là même la phrase brute
// du validateur : « String should have at most 1000 characters », en anglais,
// sur l'écran de connexion français de Vincent et Emma. C'est très exactement
// ce que la vague #847 a interdit sur l'autre porte. Le repli est désormais
// écrit par nous, et l'extraction vient de `lib/errorDetail`.

/**
 * Les surfaces où une session peut exister — et elles seules.
 *
 * `/admin` et `/admin/login`, parce que ce sont les trois consommateurs de ce
 * contexte. PAS `/chantier` : il porte sa propre clef à la main et n'a jamais
 * lu `useAuth`. PAS le site, PAS l'app invité, PAS le kiosque.
 */
export function estSurfaceAuthentifiee(chemin) {
    const c = typeof chemin === "string" ? chemin : "";
    return c === "/admin" || c.startsWith("/admin/");
}

export function AuthProvider({ children }) {
    // null = checking, false = anonymous, object = logged in
    const [user, setUser] = useState(null);
    const { pathname } = useLocation();
    const surfaceAuth = estSurfaceAuthentifiee(pathname);

    const checkSession = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/auth/me`);
            setUser(data);
        } catch (e) {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        // Hors d'une surface d'authentification, on ne demande RIEN : l'état
        // part à « anonyme », qui est la vérité, et aucune requête ne part.
        if (!surfaceAuth) {
            setUser(false);
            return;
        }
        checkSession();
    }, [surfaceAuth, checkSession]);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await axios.post(
                `${API}/api/auth/login`,
                { email, password },
            );
            setUser(data);
            return { ok: true };
        } catch (e) {
            // ⚑ LE 422 A SA PHRASE, sinon la porte devient une impasse.
            //
            // `admin.py` déclare `email: EmailStr`. Une adresse que le
            // navigateur accepte (`type="email"`) mais qu'`email-validator`
            // refuse — « vincent@manoir », sans point — rend un 422, donc une
            // LISTE, donc `null`. Vincent lisait alors « Erreur inconnue.
            // Veuillez réessayer. » et retapait la même adresse indéfiniment.
            //
            // L'ancien `formatError` affichait, lui, « value is not a valid
            // email address: The part after the @-sign … should have a period »
            // — de l'anglais moche, mais ACTIONNABLE. En le supprimant on a
            // gagné la lisibilité et perdu la piste ; on rend la piste, en
            // français. Sur CETTE route la seule cause possible d'un 422 est
            // l'adresse : le message peut donc être précis sans deviner.
            if (e?.response?.status === 422) {
                return { ok: false, error: "Cette adresse e-mail n'est pas valide." };
            }
            return {
                ok: false,
                // ⚠ PAS de repli sur `e.message` : axios y met « Request failed
                // with status code 422 », de l'anglais technique que Vincent et
                // Emma liraient sur un écran de connexion français. Le repli est
                // le nôtre — c'était déjà le cas avant, `formatError` rendant
                // cette phrase-ci dès que `detail` manquait.
                error: detailText(e) || "Erreur inconnue. Veuillez réessayer.",
            };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/api/auth/logout`);
        } catch (_) { /* noop */ }
        setUser(false);
    }, []);

    return (
        <AuthContext.Provider value={{ user, login, logout, refresh: checkSession }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
